import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is logged in on mount
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('access_token');
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { access_token } = await authAPI.login(email, password);
      localStorage.setItem('access_token', access_token);
      await fetchCurrentUser();
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);

      // Handle FastAPI validation errors (422)
      let errorMessage = 'Login failed';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // FastAPI validation error format
          errorMessage = error.response.data.detail
            .map(err => err.msg || String(err))
            .join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  const register = async (email, password, fullName) => {
    try {
      // Registration now returns a message, not a token
      // User must verify email before logging in
      const response = await authAPI.register(email, password, fullName);
      return {
        success: true,
        message: response.message,
        requiresVerification: true,
      };
    } catch (error) {
      console.error('Registration failed:', error);

      // Handle FastAPI validation errors (422)
      let errorMessage = 'Registration failed';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // FastAPI validation error format
          errorMessage = error.response.data.detail
            .map(err => `${err.loc[1]}: ${err.msg}`)
            .join(', ');
        } else {
          errorMessage = error.response.data.detail;
        }
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    setUser(null);
    setIsAuthenticated(false);
  };

  const refreshUser = async () => {
    // Re-fetch user data to get updated permissions/branding
    const token = localStorage.getItem('access_token');
    if (token) {
      await fetchCurrentUser();
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
